// The single tuning surface for the longfight map lab.
// Units are Roblox studs, 1:1 with the source mapexample.obj (200-wide corridors,
// 100-stud grid, 100-stud elevation steps) so level 1 matches the hand-built example.

import * as THREE from "three";

// ---- Grid & vertical -------------------------------------------------------
// CELL is the ONE knob for map scale: every stud dimension below derives from it, so the map
// rescales geometrically by changing this alone. 200 = doubled from the original 100 (same
// shapes, twice the size: 400-wide corridors, 200-stud elevation steps).
export const CELL = 200; // grid cell (studs). Corridors/rooms are whole multiples.
export const LEVEL_H = CELL; // vertical distance between elevation levels (studs).
export const FLOOR_BASE = CELL; // top surface height of elevation level 0.

/** Top-surface Y of a given integer elevation level. */
export const topY = (level: number): number => FLOOR_BASE + level * LEVEL_H;

// ---- Corridor / room geometry ---------------------------------------------
export const CORRIDOR_HW = CELL; // half-width of a normal corridor => 2 cells wide.
// Widened sections. Every half-width MUST be a whole multiple of CELL: the cursor is always on
// a CELL-grid line, so a non-multiple puts tile edges on half-cell lines that cellsOf()'s
// rounding snaps wrong, letting rooms silently overlap.
export const ROOM_HALF_WIDTHS = [2 * CELL, 3 * CELL];
export const STRAIGHT_CELLS: [number, number] = [2, 5]; // corridor run length, in cells.
export const ROOM_CELLS: [number, number] = [2, 4]; // room length along the path, in cells.
export const RAMP_CELLS = 3; // ramp length (3 cells rise one level, ~18.4°).
export const BRANCH_CELLS: [number, number] = [2, 3]; // dead-end branch depth, in cells.

// ---- Combat segment types (arena / core / bridge / split-level chamber) ----
export const ARENA_HALF_WIDTHS = [3 * CELL, 4 * CELL]; // open battle plazas.
export const ARENA_CELLS: [number, number] = [4, 7]; // arena length along the path, in cells.
export const CORE_HALF = 4 * CELL; // the finale core chamber half-width.
export const CORE_CELLS = 6; // core chamber length, roughly square.
export const BRIDGE_CELLS: [number, number] = [3, 6]; // elevated catwalk span, in cells.
export const DECK_THICKNESS = CELL * 0.26; // a bridge is a thin floating deck, not a solid prism.
export const CHAMBER_HW = 3 * CELL; // split-level chamber half-width.
export const CHAMBER_ROOM_CELLS: [number, number] = [2, 4]; // each half's length, in cells.

// ---- Team base platforms (large buildable plazas at each team's end) -------
// Both team start areas are big flat single-level plazas (the future player builds a base
// here). Sized in whole cells so tile edges stay on the CELL grid.
export const MAX_PLATFORM_HALF_CELLS = 8; // ceiling on plaza half-width, in cells.
export const MAX_PLATFORM_LEN_CELLS = 12; // ceiling on plaza length, in cells.
// Guaranteed floor, SHARED by both bases (start committed at ≥ this; core generation ensures room for
// at least this before placing). 4 half × 6 len = 8×6 cells = 1600 × 1200 studs.
export const MIN_PLATFORM_HALF_CELLS = 4;
export const MIN_PLATFORM_LEN_CELLS = 6;

// ---- Lanes (localized parallel sub-routes that split then remerge) ---------
// A "lane diamond": the single path opens into K parallel sub-routes of roughly equal length
// but different shape, then rejoins. Lanes are laterally separated by empty gap cells (which
// the curb system rails as drops). Local — each covers only a stretch — and can recur.
export const LANE_SLOT_CELLS = 4; // lateral cells reserved per lane (fits a jog within it).
// Empty gap cells between adjacent lane slots (the curb system rails these as drops). MUST be
// even so the total bundle width stays a whole number of cells for every lane count K — a
// half-cell edge would break cellsOf()'s grid rounding (see ROOM_HALF_WIDTHS note above).
export const LANE_SEP_CELLS = 2;
export const LANE_SPAN_CELLS: [number, number] = [4, 8]; // length of the parallel stretch, in cells.
export const LANE_PAD_CELLS = 2; // split/merge junction pad length, in cells.
export const LANE_MAX_COUNT = 3; // hard ceiling on parallel sub-routes per diamond.

// ---- Curbs (low rails along exposed edges / elevation drops) ---------------
export const CURB_H = CELL * 0.26; // curb height (studs).
export const CURB_T = CELL * 0.1; // curb thickness (studs).

// ---- Per-level scaling (pure functions of level index, tanks-style) --------
// Level 1 reproduces the example's scale; later levels get bigger and more vertical.
export const BASE_TILES = 8; // level 1 ~ the 8-part example.
export const TILES_PER_LEVEL = 3; // +3 path tiles per level.
export const MAX_TILES = 160; // safety ceiling.

export interface LevelPlan {
  level: number;
  targetTiles: number; // how many main-path tiles to lay down.
  maxElev: number; // highest elevation level reachable this level.
  boundHalf: number; // soft half-extent of the region the path roams (studs).
  pTurn: number; // base probability a step turns rather than runs straight.
  pRoom: number; // probability a straight is upgraded to a widened room.
  pRamp: number; // probability of an elevation change at a decision point.
  pBranch: number; // probability of spawning a dead-end side room after a straight.
  // combat-segment intent weights (0 disables); gated so early levels stay simple.
  wArena: number;
  wBridge: number;
  wChamber: number;
  // team base-platform sizing (whole cells).
  startHalfCells: number;
  startLenCells: number;
  coreHalfCells: number;
  coreLenCells: number;
  // lane splits.
  wLane: number; // intent weight (0 disables).
  laneMaxSplits: number; // how many lane diamonds may appear this level.
  laneCountMax: number; // max parallel sub-routes per diamond.
}

export function levelPlan(level: number): LevelPlan {
  const L = Math.max(1, Math.floor(level));
  const step = L - 1;
  const targetTiles = Math.min(BASE_TILES + step * TILES_PER_LEVEL, MAX_TILES);
  return {
    level: L,
    targetTiles,
    // Level 1 allows two elevations (0 and 1) like the example; +1 ceiling every 2 levels.
    maxElev: Math.min(1 + Math.floor(step / 2), 6),
    // Region grows so the longer path has room to wind without boxing itself in (CELL-relative).
    // Base raised to clear the large start/core plazas (up to 8 cells half-width off the origin).
    boundHalf: CELL * 14 + step * CELL * 3.2,
    pTurn: Math.min(0.3 + step * 0.01, 0.46),
    pRoom: Math.min(0.24 + step * 0.015, 0.45),
    pRamp: Math.min(0.14 + step * 0.02, 0.4),
    pBranch: Math.min(0.1 + step * 0.02, 0.4),
    // Combat segments unlock as the gauntlet lengthens, then hold at a modest weight so
    // corridors/rooms still dominate and the specials stay special.
    wArena: L >= 2 ? Math.min(0.8 + step * 0.05, 2.2) : 0,
    wBridge: L >= 3 ? Math.min(0.6 + step * 0.05, 2.0) : 0, // needs elevation to span over
    wChamber: L >= 3 ? Math.min(0.7 + step * 0.05, 2.0) : 0, // needs elevation headroom
    // Base plazas: both start at the shared MIN floor (1600×1200) and grow to the ceiling by level.
    startHalfCells: Math.min(MIN_PLATFORM_HALF_CELLS + Math.floor(step / 2), MAX_PLATFORM_HALF_CELLS),
    startLenCells: Math.min(MIN_PLATFORM_LEN_CELLS + Math.floor(step / 3), MAX_PLATFORM_LEN_CELLS),
    coreHalfCells: Math.min(MIN_PLATFORM_HALF_CELLS + Math.floor(step / 2), MAX_PLATFORM_HALF_CELLS),
    coreLenCells: Math.min(MIN_PLATFORM_LEN_CELLS + Math.floor(step / 3), MAX_PLATFORM_LEN_CELLS),
    // Lanes unlock once the gauntlet is long enough to spare the tiles; count/freq grow slowly.
    wLane: L >= 3 ? Math.min(0.6 + step * 0.06, 2.0) : 0,
    laneMaxSplits: L >= 3 ? Math.min(1 + Math.floor(step / 3), 4) : 0,
    laneCountMax: Math.min(2 + Math.floor(step / 4), LANE_MAX_COUNT),
  };
}

// ---- Palette ---------------------------------------------------------------
// Elevation reads as color: cool (low) -> warm (high), matching the top-down plan.
const LEVEL_HUES = [205, 168, 96, 45, 22, 330, 275]; // deg, one per elevation level.

export function levelColor(level: number): THREE.Color {
  const hue = LEVEL_HUES[Math.min(level, LEVEL_HUES.length - 1)] / 360;
  const light = 0.5 + Math.min(level, 5) * 0.03;
  return new THREE.Color().setHSL(hue, 0.34, light);
}

export const RAMP_COLOR = new THREE.Color(0x9a938a); // warm gray connector.
export const CURB_COLOR = new THREE.Color(0x2b2f36);
export const START_COLOR = new THREE.Color(0x35c06a);
export const EXIT_COLOR = new THREE.Color(0xff5a3c);
export const CORE_COLOR = new THREE.Color(0xff3a2a); // the enemy core objective.
export const WALL_COLOR = new THREE.Color(0x23272e); // structure walls / core plinth.
export const TEAM_BLUE = new THREE.Color(0x3a78ff); // player-side army.
export const TEAM_RED = new THREE.Color(0xff4438); // enemy army.

// ---- Scene / camera --------------------------------------------------------
export const SKY_COLOR = 0x11151b;
export const FOG_COLOR = 0x11151b;
export const CAMERA = {
  fov: 60,
  near: 5,
  far: 40000,
  // Fly speed in studs/sec (Shift = boost).
  flySpeed: 900,
  flyBoost: 3,
  lookSpeed: 0.0022,
  // Command-mode WASD ground pan: a calm base speed, Shift boosts it (was a fixed 12 with no boost).
  panSpeed: 7,
  panBoost: 3,
};

export const DEFAULT_SEED = "longfight";

// ---- Player (first-person) -------------------------------------------------
// Ported from zombies (WORLD.player*), but SHRUNK to longfight's stud world so the player is a
// little-person on the battlefield (little-people are h=23), not a giant. Every length is in studs;
// speed/jump/gravity are sized so the hop clears a small obstacle (~1× eye height) but never a
// full 200-stud level wall — you take ramps up like everyone else.
export const PLAYER = {
  eyeHeight: 20, // camera height above the feet (≈ a little-person's eye level)
  fov: 75, // first-person field of view (matches zombies' GAME_FOV; wider than the 60 spectator cam)
  radius: 8, // body radius (reserved for future unit collision)
  speed: 170, // walk speed (studs/s) — a touch nimbler than the NPC infantry (~50–160)
  sprintMul: 1.6, // Shift to sprint
  jumpSpeed: 130, // initial upward velocity (apex ≈ 25 studs)
  gravity: 340, // studs/s²
  maxHealth: 100,
  regenDelay: 4, // seconds after taking damage before health regenerates
  regenRate: 25, // health per second once regen kicks in
  stepUp: 90, // max height gain per move step (ramps ok; a full level wall of 200 blocks)
} as const;
